import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import moment from "moment"
import styles from "../form.module.css"
import { safeMoment } from "../../../utils/utils"
import { IoMdArrowDropdown, IoMdArrowDropleft, IoMdArrowDropright } from "react-icons/io"
import { BsCheckLg, BsClock } from "react-icons/bs"
import Month from "./Month";
import Button from './../Button'

const DatePicker = ({ name, dateStart, dateEnd, timeStart, timeEnd, onChange, min, max, multiple, theme, showTime = false }) => {
    const initialDate = multiple ? (dateEnd ? safeMoment(dateEnd) : (dateStart ? safeMoment(dateStart) : moment())) : (dateStart ? safeMoment(dateStart) : moment())
    const [tempDateStart, setTempDateStart] = useState(dateStart)
    const [tempDateEnd, setTempDateEnd] = useState(dateEnd)
    const [tempTimeStart, setTempTimeStart] = useState(timeStart)
    const [tempTimeEnd, setTempTimeEnd] = useState(timeEnd)
    const [selectedYear, setSelectedYear] = useState(safeMoment(initialDate).format("YYYY"))
    const [selectedMonth, setSelectedMonth] = useState(safeMoment(initialDate).format("M"))
    const [listPresets, setListPresets] = useState([])
    const [active, setActive] = useState(false)
    const [activeTimeMenu, setActiveTimeMenu] = useState("")
    const [timeMenuStyle, setTimeMenuStyle] = useState({})
    const [stylesSelect, setStylesSelect] = useState({})
    const inputRef = useRef()
    const optionsRef = useRef()
    const timeTriggerRefs = useRef({})
    const timeMenuRef = useRef()

    useEffect(() => {
        if (multiple) {
            loadListPresets()
        }
    }, [min, max])

    useLayoutEffect(() => {
        if (!active) return

        updateDropdownPosition()

        window.addEventListener("resize", updateDropdownPosition)
        window.addEventListener("scroll", updateDropdownPosition, true)

        return () => {
            window.removeEventListener("resize", updateDropdownPosition)
            window.removeEventListener("scroll", updateDropdownPosition, true)
        }
    }, [active, selectedMonth, selectedYear, multiple])

    useLayoutEffect(() => {
        if (!activeTimeMenu) return

        updateTimeMenuPosition()

        window.addEventListener("resize", updateTimeMenuPosition)
        window.addEventListener("scroll", updateTimeMenuPosition, true)

        return () => {
            window.removeEventListener("resize", updateTimeMenuPosition)
            window.removeEventListener("scroll", updateTimeMenuPosition, true)
        }
    }, [activeTimeMenu])

    const getTextByDates = () => {
        let titleInput = name
        if (multiple) {
            if (dateStart && dateEnd) {
                titleInput = `${safeMoment(dateStart).format("DD/MM/YYYY")} a ${safeMoment(dateEnd).format("DD/MM/YYYY")}`
                if (showTime && (timeStart || timeEnd)) {
                    titleInput = `${safeMoment(dateStart).format("DD/MM/YYYY")} ${timeStart || "00:00"} a ${safeMoment(dateEnd).format("DD/MM/YYYY")} ${timeEnd || "23:59"}`
                }
            }
        } else {
            if (dateStart) {
                titleInput = safeMoment(dateStart).format("DD/MM/YYYY")
                if (showTime && timeStart) {
                    titleInput = `${titleInput} ${timeStart}`
                }
            }
        }
        return titleInput
    }

    const handleToogleVisibleOptions = () => {
        setActive(previousActive => {
            if (previousActive) {
                setTempDateStart(dateStart)
                setTempDateEnd(dateEnd)
                setTempTimeStart(timeStart)
                setTempTimeEnd(timeEnd)
                setActiveTimeMenu("")
                setSelectedYear(safeMoment(initialDate).format("YYYY"))
                setSelectedMonth(safeMoment(initialDate).format("M"))
            } else {
                setStylesSelect({ visibility: "hidden" })
            }

            return !previousActive
        })
    }

    const updateDropdownPosition = () => {
        if (!inputRef.current || !optionsRef.current) return

        const margin = 8
        const trigger = inputRef.current.getBoundingClientRect()
        const dropdown = optionsRef.current
        const maxWidth = window.innerWidth - (margin * 2)
        const maxHeight = window.innerHeight - (margin * 2)
        const dropdownWidth = Math.min(dropdown.offsetWidth || maxWidth, maxWidth)
        const dropdownHeight = Math.min(dropdown.offsetHeight || maxHeight, maxHeight)
        const spaceBelow = window.innerHeight - trigger.bottom - margin
        const spaceAbove = trigger.top - margin

        let top = trigger.bottom + margin
        if (dropdownHeight > spaceBelow && spaceAbove > spaceBelow) {
            top = trigger.top - dropdownHeight - margin
        }
        top = Math.min(Math.max(top, margin), window.innerHeight - dropdownHeight - margin)

        let left = trigger.left
        if (left + dropdownWidth > window.innerWidth - margin) {
            left = window.innerWidth - dropdownWidth - margin
        }
        left = Math.max(left, margin)

        setStylesSelect({
            top: `${top}px`,
            left: `${left}px`,
            maxWidth: `${maxWidth}px`,
            maxHeight: `${maxHeight}px`,
            visibility: "visible",
        })
    }

    const updateTimeMenuPosition = () => {
        const trigger = timeTriggerRefs.current?.[activeTimeMenu]
        const menu = timeMenuRef.current
        if (!trigger || !menu) return

        const margin = 8
        const triggerRect = trigger.getBoundingClientRect()
        const maxWidth = window.innerWidth - (margin * 2)
        const maxHeight = window.innerHeight - (margin * 2)
        const spaceBelow = window.innerHeight - triggerRect.bottom - margin
        const spaceAbove = triggerRect.top - margin
        const openAbove = spaceBelow < 240 && spaceAbove > spaceBelow
        const availableHeight = Math.max(80, openAbove ? spaceAbove - 6 : spaceBelow - 6)
        const menuMaxHeight = Math.min(240, availableHeight, maxHeight)
        const menuWidth = Math.min(menu.offsetWidth || 128, maxWidth)
        const menuHeight = Math.min(menu.offsetHeight || menuMaxHeight, menuMaxHeight)

        let top = triggerRect.bottom + 6
        if (openAbove) {
            top = triggerRect.top - menuHeight - 6
        }
        top = Math.min(Math.max(top, margin), window.innerHeight - menuHeight - margin)

        let left = triggerRect.left
        if (left + menuWidth > window.innerWidth - margin) {
            left = window.innerWidth - menuWidth - margin
        }
        left = Math.max(left, margin)

        setTimeMenuStyle({
            top: `${top}px`,
            left: `${left}px`,
            maxHeight: `${menuMaxHeight}px`,
            visibility: "visible",
        })
    }

    const onChangeDates = () => {
        if (!tempDateEnd) {
            setTempDateEnd(tempDateStart)
            onChange(tempDateStart, tempDateStart, tempTimeStart, tempTimeEnd, { preset: null })
        } else {
            onChange(tempDateStart, tempDateEnd, tempTimeStart, tempTimeEnd, { preset: null })
        }
        setActive(false)
        setActiveTimeMenu("")
    }

    const getDaysByMonth = (year, month) => {
        const firstDayOfMonth = safeMoment(`${year}-${month}-01`, "YYYY-MM-DD").format("YYYY-MM-DD")
        const lastDayOfMonth = parseInt(safeMoment(firstDayOfMonth, "YYYY-MM-DD").endOf("month").format("D"))
        const daysUntilSunday = safeMoment(firstDayOfMonth, "YYYY-MM-DD").format("d")
        const days = []
        let week = []

        for (let i = 0; i < daysUntilSunday; i++) {
            week.push({ dayOfMonth: null, dayOfWeek: null, inRange: false, active: false, disabled: true })
        }
        for (let i = 1; i <= lastDayOfMonth; i++) {
            const fullDay = safeMoment(`${year}-${month}-${i}`, "YYYY-MM-DD").format("YYYY-MM-DD")
            const dayOfWeek = safeMoment(fullDay, "YYYY-MM-DD").format("d")
            const inRange = multiple ? tempDateStart && tempDateEnd ? safeMoment(fullDay, "YYYY-MM-DD").isBetween(tempDateStart, tempDateEnd) : false : false
            const isStart = safeMoment(fullDay, "YYYY-MM-DD").isSame(tempDateStart)
            const isEnd = multiple ? safeMoment(fullDay, "YYYY-MM-DD").isSame(tempDateEnd) : false
            const today = safeMoment(fullDay, "YYYY-MM-DD").isSame(moment().format("YYYY-MM-DD"))
            const minIsOk = min ? safeMoment(fullDay, "YYYY-MM-DD").isSameOrAfter(min) : true
            const maxIsOk = max ? safeMoment(fullDay, "YYYY-MM-DD").isSameOrBefore(max) : true
            const disabled = !(minIsOk && maxIsOk)
            const firstDay = i === 1
            const lastDay = i === lastDayOfMonth
            week.push({ dayOfMonth: i, dayOfWeek: dayOfWeek, fullDay, inRange, isStart, isEnd, today, disabled, firstDay, lastDay })

            if (parseInt(dayOfWeek) === 6) {
                days.push(week)
                week = []
            }
        }
        if (week.length > 0) {
            days.push(week)
        }

        return { firstDayOfMonth, days }
    }

    const getMonthsByDays = () => {
        const listDays = multiple ? [getDaysByMonth(selectedMonth > 1 ? selectedYear : selectedYear - 1, selectedMonth > 1 ? selectedMonth - 1 : 12), getDaysByMonth(selectedYear, selectedMonth)] : [getDaysByMonth(selectedYear, selectedMonth)]
        return (
            listDays.map((month, index) => (
                <Month month={month} key={index} handleSelectDates={handleSelectDates} />
            ))
        )
    }

    const decrementMonthSelected = () => {
        setSelectedMonth(prevMonth => {
            const newMonth = safeMoment(`${selectedYear}-${prevMonth}-10`, "YYYY-MM-DD").subtract(1, 'M').format("M")
            return newMonth
        })
        setSelectedYear(prevYear => {
            const newYear = safeMoment(`${prevYear}-${selectedMonth}-10`, "YYYY-MM-DD").subtract(1, 'M').format("YYYY")
            return newYear
        })
    }

    const incrementMonthSelected = () => {
        setSelectedMonth(prevMonth => {
            const newMonth = safeMoment(`${selectedYear}-${prevMonth}-10`, "YYYY-MM-DD").add(1, 'M').format("M")
            return newMonth
        })
        setSelectedYear(prevYear => {
            const newYear = safeMoment(`${prevYear}-${selectedMonth}-10`, "YYYY-MM-DD").add(1, 'M').format("YYYY")
            return newYear
        })
    }

    const handleSelectDates = (newDate) => {
        if (multiple) {
            if ((!tempDateStart && !tempDateEnd) || (tempDateStart && tempDateEnd)) {
                setTempDateStart(newDate)
                setTempDateEnd(undefined)
            } else {
                if (safeMoment(tempDateStart).isAfter(newDate)) {
                    setTempDateEnd(tempDateStart)
                    setTempDateStart(newDate)
                } else {
                    setTempDateEnd(newDate)
                }
            }
        } else {
            setTempDateStart(newDate)
        }
    }

    const getPositionInput = () => {
        const width = inputRef.current.offsetWidth
        const left = inputRef.current.offsetLeft

        return width + left
    }

    const alignInputInRight = () => {
        const bodyWidth = document.querySelector("body")?.offsetWidth ?? 0
        const position = getPositionInput()

        return position > (bodyWidth / 2) ? true : false
    }

    const loadListPresets = () => {
        const weekDayToday = parseInt(moment().format("d"))
        const monthDayToday = parseInt(moment().format("D"))
        const today = moment().format("YYYY-MM-DD")
        const yesterday = moment().subtract(1, 'd').format("YYYY-MM-DD")
        const last7Days = moment().subtract(7, 'd').format("YYYY-MM-DD")
        const last14Days = moment().subtract(14, 'd').format("YYYY-MM-DD")
        const last30Days = moment().subtract(30, 'd').format("YYYY-MM-DD")
        const startWeek = moment().subtract(weekDayToday, 'd').format("YYYY-MM-DD")
        const startLastWeek = moment().subtract(weekDayToday + 7, 'd').format("YYYY-MM-DD")
        const endLastWeek = moment().subtract(weekDayToday + 1, 'd').format("YYYY-MM-DD")
        const startMonth = moment().startOf('M').format("YYYY-MM-DD")
        const endMonth = moment().endOf('M').isSameOrBefore(max) ? moment().endOf('M').format("YYYY-MM-DD") : today
        const startLastMonth = moment(startMonth, "YYYY-MM-DD").subtract(1, 'M').format("YYYY-MM-DD")
        const endLastMonth = moment().subtract(monthDayToday, 'd').format("YYYY-MM-DD")

        setListPresets([
            { name: 'all', title: "Todo o período", required: true },
            { name: 'today', title: "Hoje", dateStart: today, dateEnd: today },
            { name: 'yesterday', title: "Ontem", dateStart: yesterday, dateEnd: yesterday },
            { name: 'last7Days', title: "Últimos 7 dias", dateStart: last7Days, dateEnd: yesterday },
            { name: 'last14Days', title: "Últimos 14 dias", dateStart: last14Days, dateEnd: yesterday },
            { name: 'last30Days', title: "Últimos 30 dias", dateStart: last30Days, dateEnd: yesterday },
            { name: 'thisWeek', title: "Esta semana", dateStart: startWeek, dateEnd: today },
            { name: 'lastWeek', title: "Semana passada", dateStart: startLastWeek, dateEnd: endLastWeek },
            { name: 'thisMonth', title: "Este mês", dateStart: startMonth, dateEnd: endMonth },
            { name: 'lastMonth', title: "Mês passado", dateStart: startLastMonth, dateEnd: endLastMonth },
        ])
    }

    const getPresetsByCalendar = () => {
        return listPresets?.filter(preset => {
            return preset.required || ((!min || safeMoment(preset.dateStart, "YYYY-MM-DD").isSameOrAfter(min)) && (!max || safeMoment(preset.dateEnd, "YYYY-MM-DD").isSameOrBefore(max)))
        }).map((preset, index) => {
            const active = (!preset.dateStart && !dateStart && !preset.dateEnd && !dateEnd) || (safeMoment(preset.dateStart, "YYYY-MM-DD").isSame(dateStart) && safeMoment(preset.dateEnd, "YYYY-MM-DD").isSame(dateEnd))

            return (
                <div key={index} className={`${active && styles.active}`} onClick={() => handlePresetSelection(preset)}>
                    {active && <BsCheckLg />}
                    <span>{preset.title}</span>
                </div>
            )
        })
    }

    const handlePresetSelection = preset => {
        const { dateStart, dateEnd } = preset
        setTempDateStart(dateStart)
        setTempDateEnd(dateEnd)
        setTempTimeStart(undefined)
        setTempTimeEnd(undefined)
        onChange(dateStart, dateEnd, undefined, undefined, { preset: preset.name })
        setActive(false)
        setActiveTimeMenu("")
    }

    const getTimeOptions = () => {
        const options = []
        const step = 15
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += step) {
                options.push(`${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`)
            }
        }
        return options
    }

    const handleSelectTime = (field, value) => {
        if (field === "start") {
            setTempTimeStart(value)
        } else {
            setTempTimeEnd(value)
        }
        setActiveTimeMenu("")
    }

    const handleToggleTimeMenu = (field) => {
        setActiveTimeMenu(previousValue => {
            const nextValue = previousValue === field ? "" : field
            if (nextValue) {
                setTimeMenuStyle({ visibility: "hidden" })
            }
            return nextValue
        })
    }

    const renderTimeMenu = () => {
        if (!activeTimeMenu) return null
        const value = activeTimeMenu === "start" ? tempTimeStart : tempTimeEnd

        return createPortal(
            <div ref={timeMenuRef} className={styles.timeSelectMenu} style={timeMenuStyle}>
                <button type="button" className={styles.timeSelectOption} onClick={() => handleSelectTime(activeTimeMenu, "")}>Sem horário</button>
                {getTimeOptions().map(option => (
                    <button
                        type="button"
                        key={option}
                        className={`${styles.timeSelectOption} ${value === option ? styles.active : ""}`}
                        onClick={() => handleSelectTime(activeTimeMenu, option)}
                    >
                        {value === option && <BsCheckLg />}
                        <span>{option}</span>
                    </button>
                ))}
            </div>,
            document.body
        )
    }

    const renderTimeSelect = ({ field, label, value, placeholder }) => (
        <label className={styles.timeSelect}>
            <span>{label}</span>
            <button
                type="button"
                ref={(element) => { timeTriggerRefs.current[field] = element }}
                className={`${styles.timeSelectTrigger} ${activeTimeMenu === field ? styles.active : ""}`}
                onClick={() => handleToggleTimeMenu(field)}
            >
                <span>{value || placeholder}</span>
                <BsClock />
            </button>
        </label>
    )

    return (
        <>
            <div className={`${styles.datePicker} ${active && styles.active} ${styles[theme]}`} ref={inputRef}>
                <div className={styles.text} onClick={handleToogleVisibleOptions} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleToogleVisibleOptions()} tabIndex="0" role="button">
                    <span>
                        {getTextByDates()}
                    </span>
                    <IoMdArrowDropdown />
                </div>
            </div>
            {active && createPortal(
                <div className={`${styles.datePicker} ${styles.pickerPortal}`}>
                    <div className={styles.backgroundForm} onClick={handleToogleVisibleOptions} />
                    <div ref={optionsRef} className={`${styles.groupOptions} ${alignInputInRight() ? styles.right : styles.left}`} style={stylesSelect}>
                        <div className={styles.calendar}>
                            <div className={styles.top}>
                                <div className={styles.header}>
                                    <div className={styles.arrows}>
                                        <IoMdArrowDropleft className={styles.arrow} onClick={decrementMonthSelected} />
                                        <IoMdArrowDropright className={styles.arrow} onClick={incrementMonthSelected} />
                                    </div>
                                </div>
                                <div className={styles.body}>
                                    {getMonthsByDays()}
                                </div>
                            </div>
                            <div className={styles.footer}>
                                <div className={styles.selectedPeriod}>
                                    {tempDateStart &&
                                        <span>
                                            {safeMoment(tempDateStart).format("DD [de] MMM [de] YYYY")} {showTime && tempTimeStart ? tempTimeStart : ""}
                                        </span>
                                    }
                                    {multiple && tempDateEnd &&
                                        <>
                                            <span>a</span>
                                            <span>
                                                {safeMoment(tempDateEnd).format("DD [de] MMM [de] YYYY")} {showTime && tempTimeEnd ? tempTimeEnd : ""}
                                            </span>
                                        </>
                                    }
                                </div>
                                {showTime &&
                                    <div className={styles.timeRange}>
                                        {renderTimeSelect({ field: "start", label: "Início", value: tempTimeStart, placeholder: "00:00" })}
                                        {multiple &&
                                            renderTimeSelect({ field: "end", label: "Fim", value: tempTimeEnd, placeholder: "23:59" })
                                        }
                                    </div>
                                }
                                <div className={styles.actions}>
                                    <Button variant="secondary" onClick={handleToogleVisibleOptions}>
                                        Cancelar
                                    </Button>
                                    <Button variant="primary" onClick={onChangeDates}>
                                        Selecionar
                                    </Button>
                                </div>
                            </div>
                        </div>
                        {multiple &&
                            <div className={styles.panelRight}>
                                <span>Períodos</span>
                                <div className={styles.presets}>
                                    {getPresetsByCalendar()}
                                </div>
                            </div>
                        }
                    </div>
                </div>,
                document.body
            )}
            {renderTimeMenu()}
        </>
    )
}

export default DatePicker
